/**
 * BatchStatusBanner — the batch gate's facts, in the server's words (EXR-W1 W1-10, EXV-003).
 * =================================================================================
 * Fed by the `gate` block every Execution list row and batch payload carries
 * ({code, hold: {placed_by, at, reason, ticket_id}, waiting_on: [{role, since}], ended}).
 * The client decides nothing: the code names which fact applies, the text is the server's facts.
 *
 *   BATCH_ON_HOLD       On hold — placed by X, <date>, reason R, ticket #id
 *   ISSUE_NOT_APPROVED  Awaiting issue approval — waiting on <role> since <date>, …
 *   BATCH_ENDED         Batch ended (<header word>)
 *
 * `compact` → a one-line strip for a table row; otherwise a StatusNotice for the batch record header.
 * Renders nothing when there is no gate fact.
 */
import React from 'react';
import { theme } from 'antd';
import StatusIndicator, { StatusNotice } from '@/components/genealogy/StatusIndicator';
import { formatDateTime } from '@/utils/datetime';

const STATE_FOR_CODE = {
  BATCH_ON_HOLD: 'hold',
  ISSUE_NOT_APPROVED: 'pending',
  BATCH_ENDED: 'error',
};

const join = (parts) => parts.filter(Boolean).join(', ');

/** The banner's title and detail line, from the gate block only. */
export const bannerText = (gate, statusWord) => {
  if (!gate || !gate.code) return null;
  if (gate.code === 'BATCH_ON_HOLD') {
    const h = gate.hold || {};
    return {
      title: statusWord || 'On hold',
      detail: join([
        h.placed_by ? `placed by ${h.placed_by}` : null,
        h.at ? formatDateTime(h.at) : null,
        h.reason ? `reason ${h.reason}` : null,
        h.ticket_id ? `ticket #${h.ticket_id}` : null,
      ]),
    };
  }
  if (gate.code === 'ISSUE_NOT_APPROVED') {
    const rows = gate.waiting_on || [];
    return {
      title: statusWord || 'Awaiting issue approval',
      detail: rows.length
        ? `waiting on ${rows
            .map((w) => `${w.role}${w.since ? ` since ${formatDateTime(w.since)}` : ''}`)
            .join('; ')}`
        : '',
    };
  }
  if (gate.code === 'BATCH_ENDED') {
    return { title: `Batch ended${gate.ended ? ` (${gate.ended})` : ''}`, detail: '' };
  }
  return null;
};

const BatchStatusBanner = ({ gate, statusWord, compact = false, style }) => {
  const { token } = theme.useToken();
  const text = bannerText(gate, statusWord);
  if (!text) return null;
  const state = STATE_FOR_CODE[gate.code] || 'neutral';
  if (compact) {
    return (
      <div
        data-testid="batch-status-banner"
        style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary, lineHeight: 1.4, ...style }}
      >
        <StatusIndicator state={state} label={text.detail ? `${text.title} — ${text.detail}` : text.title} />
      </div>
    );
  }
  return (
    <div data-testid="batch-status-banner" style={style}>
      <StatusNotice state={state} title={text.title}>
        {text.detail || null}
      </StatusNotice>
    </div>
  );
};

export default BatchStatusBanner;
